import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../config/api';
import { FaArrowLeft, FaSearch } from 'react-icons/fa';
import { MEMBERSHIP_SEASON } from '../../config/membershipSeason';
import { MEMBER_ACTIVE_SEASONS, isActiveMemberForSeason } from '../../utils/memberStatus';
import {
  buildMembershipCodesBySeasonFromUser,
  getMembershipCodesForSeason,
} from '../../utils/membershipCodes';
import { getIdentityExpiryStatus } from '../../utils/identityExpiry';
import './AdminPages.css';
import './AdminMemberSeasonCodes.css';

function formatIdentityExpiryDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function identityExpiryLabel(status, expiryDate) {
  const dateStr = formatIdentityExpiryDate(expiryDate);
  switch (status) {
    case 'expired':
      return { badge: 'Échue', title: dateStr ? `Expirée le ${dateStr}` : 'Document échu' };
    case 'soon':
      return { badge: 'Proche', title: dateStr ? `Expire le ${dateStr}` : 'Échéance proche' };
    case 'ok':
      return { badge: 'OK', title: dateStr ? `Valide jusqu’au ${dateStr}` : 'Document valide' };
    default:
      return { badge: '—', title: 'Date d’échéance non renseignée' };
  }
}

function rowDraftFromUser(user, season) {
  const codes = getMembershipCodesForSeason(user, season);
  return {
    pnrCode: codes.pnrCode || '',
    ecardCode: codes.ecardCode || '',
    jcardNumber: user?.juventusFanCard?.number || '',
  };
}

function draftsEqual(a, b) {
  return (
    a.pnrCode === b.pnrCode
    && a.ecardCode === b.ecardCode
    && a.jcardNumber === b.jcardNumber
  );
}

const AdminMemberSeasonCodes = () => {
  const navigate = useNavigate();
  const [season, setSeason] = useState(MEMBERSHIP_SEASON);
  const [users, setUsers] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [onlyActiveMembers, setOnlyActiveMembers] = useState(true);
  const [onlyIdentityAlert, setOnlyIdentityAlert] = useState(false);
  const [savingUserId, setSavingUserId] = useState(null);
  const [saveErrors, setSaveErrors] = useState({});
  const [identityModalUser, setIdentityModalUser] = useState(null);
  const [identityExpiryDate, setIdentityExpiryDate] = useState('');
  const [identityFileF, setIdentityFileF] = useState(null);
  const [identityFileBack, setIdentityFileBack] = useState(null);
  const [identitySubmitting, setIdentitySubmitting] = useState(false);
  const [identityFormError, setIdentityFormError] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/users', { params: { season } });
      const list = Array.isArray(data) ? data : [];
      setUsers(list);
      const nextDrafts = {};
      for (const u of list) {
        nextDrafts[u._id] = rowDraftFromUser(u, season);
      }
      setDrafts(nextDrafts);
      setSaveErrors({});
    } catch (err) {
      console.error(err);
      alert('Erreur lors du chargement des utilisateurs');
      setUsers([]);
      setDrafts({});
    } finally {
      setLoading(false);
    }
  }, [season]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users
      .filter((u) => {
        if (onlyActiveMembers && !isActiveMemberForSeason(u, season)) return false;
        if (onlyIdentityAlert) {
          const st = getIdentityExpiryStatus(u);
          if (st !== 'expired' && st !== 'soon') return false;
        }
        if (!q) return true;
        const hay = [
          u.firstName,
          u.lastName,
          u.email,
          getMembershipCodesForSeason(u, season).ecardCode,
          getMembershipCodesForSeason(u, season).pnrCode,
          u.juventusFanCard?.number,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => {
        const ln = (a.lastName || '').localeCompare(b.lastName || '', 'fr');
        if (ln !== 0) return ln;
        return (a.firstName || '').localeCompare(b.firstName || '', 'fr');
      });
  }, [users, search, onlyActiveMembers, onlyIdentityAlert, season]);

  const updateDraft = (userId, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [userId]: { ...(prev[userId] || {}), [field]: value },
    }));
    setSaveErrors((prev) => {
      if (!prev[userId]) return prev;
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  const openIdentityModal = (user) => {
    setIdentityModalUser(user);
    setIdentityExpiryDate('');
    setIdentityFileF(null);
    setIdentityFileBack(null);
    setIdentityFormError('');
  };

  const closeIdentityModal = () => {
    setIdentityModalUser(null);
    setIdentityExpiryDate('');
    setIdentityFileF(null);
    setIdentityFileBack(null);
    setIdentityFormError('');
  };

  const submitIdentityUpdate = async (e) => {
    e.preventDefault();
    if (!identityModalUser) return;
    if (!identityFileF) {
      setIdentityFormError('Le scan recto est obligatoire.');
      return;
    }
    if (!identityExpiryDate) {
      setIdentityFormError("La date d'échéance est obligatoire.");
      return;
    }

    const formData = new FormData();
    formData.append('identityFileF', identityFileF);
    if (identityFileBack) {
      formData.append('identityFileBack', identityFileBack);
    }
    formData.append('expiryDate', identityExpiryDate);

    try {
      setIdentitySubmitting(true);
      setIdentityFormError('');
      const { data } = await api.put(
        `/users/${identityModalUser._id}/identity-documents`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const updated = data?.user;
      if (updated?._id) {
        setUsers((prev) => prev.map((u) => (u._id === updated._id ? { ...u, ...updated } : u)));
      } else {
        await loadUsers();
      }
      closeIdentityModal();
    } catch (err) {
      console.error(err);
      setIdentityFormError(
        err.response?.data?.error || "Erreur lors de la mise à jour de la pièce d'identité",
      );
    } finally {
      setIdentitySubmitting(false);
    }
  };

  const saveRow = async (user) => {
    const userId = user._id;
    const draft = drafts[userId];
    if (!draft) return;

    const baseline = rowDraftFromUser(user, season);
    if (draftsEqual(draft, baseline)) return;

    try {
      setSavingUserId(userId);
      const membershipCodesBySeason = buildMembershipCodesBySeasonFromUser(user);
      membershipCodesBySeason[season] = {
        ...(membershipCodesBySeason[season] || {}),
        ecardCode: draft.ecardCode.trim(),
        pnrCode: draft.pnrCode.trim(),
      };

      const jcardTrim = draft.jcardNumber.trim();
      const payload = {
        membershipCodesBySeason,
        juventusFanCard: {
          ...(user.juventusFanCard || {}),
          number: jcardTrim,
          has: jcardTrim ? true : Boolean(user.juventusFanCard?.has),
        },
      };

      const { data } = await api.put(`/users/${userId}`, payload);
      const updated = data?.user || data;
      if (updated && updated._id) {
        setUsers((prev) => prev.map((u) => (u._id === userId ? { ...u, ...updated } : u)));
        setDrafts((prev) => ({
          ...prev,
          [userId]: rowDraftFromUser(updated, season),
        }));
      } else {
        await loadUsers();
      }
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.error || 'Erreur lors de l’enregistrement';
      setSaveErrors((prev) => ({ ...prev, [userId]: msg }));
    } finally {
      setSavingUserId(null);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-page-header">
          <button
            type="button"
            className="btn-back"
            onClick={() => navigate('/admin')}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginRight: '1rem',
              color: 'var(--juve-black)',
            }}
          >
            <FaArrowLeft /> Retour
          </button>
          <h1 style={{ flex: 1 }}>Codes membres par saison</h1>
        </div>

        <p className="member-season-codes-hint">
          Saisie rapide des codes PNR, e-card et numéro Juventus Card. Un bouton « Sauvegarder » apparaît
          sur chaque ligne dès qu’une valeur est modifiée.
        </p>

        <div className="member-season-codes-toolbar">
          <label htmlFor="season-select">
            Saison
            <select
              id="season-select"
              value={season}
              onChange={(e) => setSeason(e.target.value)}
            >
              {MEMBER_ACTIVE_SEASONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>

          <label htmlFor="member-search">
            Recherche
            <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              <FaSearch
                aria-hidden
                style={{ position: 'absolute', left: '0.5rem', color: '#888', pointerEvents: 'none' }}
              />
              <input
                id="member-search"
                type="search"
                placeholder="Nom, email, code…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ paddingLeft: '1.75rem', minWidth: '14rem' }}
              />
            </span>
          </label>

          <label className="filter-checkbox">
            <input
              type="checkbox"
              checked={onlyActiveMembers}
              onChange={(e) => setOnlyActiveMembers(e.target.checked)}
            />
            Membres actifs uniquement
          </label>

          <label className="filter-checkbox">
            <input
              type="checkbox"
              checked={onlyIdentityAlert}
              onChange={(e) => setOnlyIdentityAlert(e.target.checked)}
            />
            Carte identité échue ou proche (30 j.)
          </label>

          <span className="member-season-codes-count">
            {loading ? 'Chargement…' : `${filteredUsers.length} ligne(s)`}
          </span>
        </div>

        <div className="admin-table-container member-season-codes-table">
          {loading ? (
            <p style={{ padding: '2rem' }}>Chargement des membres…</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Prénom</th>
                  <th>Email</th>
                  <th>Code PNR</th>
                  <th>E-card</th>
                  <th>J-card (n°)</th>
                  <th>Carte identité</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ padding: '1.5rem', textAlign: 'center', color: '#666' }}>
                      Aucun membre pour ces critères.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => {
                    const userId = user._id;
                    const draft = drafts[userId] || rowDraftFromUser(user, season);
                    const saving = savingUserId === userId;
                    const err = saveErrors[userId];
                    const idStatus = getIdentityExpiryStatus(user);
                    const idMeta = identityExpiryLabel(idStatus, user.identity?.expiryDate);
                    const baseline = rowDraftFromUser(user, season);
                    const dirty = !draftsEqual(draft, baseline);
                    const inputClass = (extra) =>
                      ['code-input', saving ? 'saving' : '', err ? 'error' : '', dirty ? 'dirty' : '', extra]
                        .filter(Boolean)
                        .join(' ');

                    return (
                      <tr
                        key={userId}
                        className={[saving ? 'row-saving' : '', dirty ? 'row-dirty' : '']
                          .filter(Boolean)
                          .join(' ') || undefined}
                      >
                        <td>{user.lastName || '—'}</td>
                        <td>{user.firstName || '—'}</td>
                        <td style={{ fontSize: '0.85rem' }}>{user.email || '—'}</td>
                        <td>
                          <input
                            type="text"
                            className={inputClass()}
                            value={draft.pnrCode}
                            disabled={saving}
                            onChange={(e) => updateDraft(userId, 'pnrCode', e.target.value)}
                            autoComplete="off"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className={inputClass()}
                            value={draft.ecardCode}
                            disabled={saving}
                            onChange={(e) => updateDraft(userId, 'ecardCode', e.target.value)}
                            autoComplete="off"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className={inputClass()}
                            value={draft.jcardNumber}
                            disabled={saving}
                            onChange={(e) => updateDraft(userId, 'jcardNumber', e.target.value)}
                            autoComplete="off"
                          />
                        </td>
                        <td>
                          <span
                            className={`identity-expiry-badge ${idStatus}`}
                            title={idMeta.title}
                          >
                            {idMeta.badge}
                          </span>
                          {user.identity?.expiryDate && (
                            <span className="identity-expiry-date">
                              {formatIdentityExpiryDate(user.identity.expiryDate)}
                            </span>
                          )}
                          {(idStatus === 'expired' || idStatus === 'soon') && (
                            <button
                              type="button"
                              className={`member-season-codes-identity-btn${idStatus === 'expired' ? ' is-expired' : ''}`}
                              onClick={() => openIdentityModal(user)}
                            >
                              Mettre à jour
                            </button>
                          )}
                          {err && (
                            <span className="identity-expiry-date" style={{ color: '#c0392b' }}>
                              {err}
                            </span>
                          )}
                        </td>
                        <td className="member-season-codes-actions">
                          {dirty ? (
                            <button
                              type="button"
                              className="member-season-codes-save-btn"
                              disabled={saving}
                              onClick={() => saveRow(user)}
                            >
                              {saving ? 'Enregistrement…' : 'Sauvegarder'}
                            </button>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>

        {identityModalUser && (
          <div
            className="modal-overlay"
            role="presentation"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeIdentityModal();
            }}
          >
            <div className="modal-content member-season-codes-identity-modal" role="dialog" aria-modal="true">
              <h2>Mettre à jour la pièce d&apos;identité</h2>
              <p className="member-season-codes-identity-modal-intro">
                {identityModalUser.firstName} {identityModalUser.lastName}
                {identityModalUser.email ? ` — ${identityModalUser.email}` : ''}
              </p>
              <form onSubmit={submitIdentityUpdate}>
                <div className="member-season-codes-identity-field">
                  <label htmlFor="identity-expiry-date">Nouvelle date d&apos;échéance *</label>
                  <input
                    id="identity-expiry-date"
                    type="date"
                    value={identityExpiryDate}
                    onChange={(e) => setIdentityExpiryDate(e.target.value)}
                    required
                    disabled={identitySubmitting}
                  />
                </div>
                <div className="member-season-codes-identity-field">
                  <label htmlFor="identity-file-f">Scan recto * (JPG, PNG, PDF, HEIC)</label>
                  <input
                    id="identity-file-f"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf,.heic,.heif,image/*,application/pdf"
                    onChange={(e) => setIdentityFileF(e.target.files?.[0] || null)}
                    required
                    disabled={identitySubmitting}
                  />
                </div>
                <div className="member-season-codes-identity-field">
                  <label htmlFor="identity-file-back">Scan verso (optionnel)</label>
                  <input
                    id="identity-file-back"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf,.heic,.heif,image/*,application/pdf"
                    onChange={(e) => setIdentityFileBack(e.target.files?.[0] || null)}
                    disabled={identitySubmitting}
                  />
                </div>
                {identityFormError && (
                  <p className="member-season-codes-identity-error" role="alert">
                    {identityFormError}
                  </p>
                )}
                <div className="member-season-codes-identity-actions">
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={closeIdentityModal}
                    disabled={identitySubmitting}
                  >
                    Annuler
                  </button>
                  <button type="submit" className="btn-add" disabled={identitySubmitting}>
                    {identitySubmitting ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMemberSeasonCodes;
